import React, { useEffect, useRef, useState } from 'react';

import { Center, Flex, Progress, Stack, StackProps, Text, UnstyledButton, useMantineTheme } from '@mantine/core';
import { IconPlayerPauseFilled, IconPlayerPlayFilled, IconReload } from '@tabler/icons-react';
import { MILLISECONDS_SECONDS_FACTOR } from 'shared/constants/general-constants';
import { formatSecondsToMinutesAndSeconds } from 'shared/utils/date';

const REPLAY_DELAY = 300;

interface AudioPlayerProps extends StackProps {
  audioUrl: string;
  color?: string;
}

export const AudioPlayer = ({ audioUrl, color = 'black', ...stackProps }: AudioPlayerProps) => {
  const theme = useMantineTheme();

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [displayTime, setDisplayTime] = useState(0);

  const audioRef = useRef<HTMLAudioElement>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number>();
  const isRestartingRef = useRef(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setDisplayTime(audio.currentTime);
      lastUpdateTimeRef.current = Date.now();
    };

    const handleLoadedMetadata = () => setDuration(audio.duration);

    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    const animate = () => {
      if (isPlaying && audioRef.current && !audioRef.current.paused && !isRestartingRef.current) {
        const now = Date.now();
        const elapsed = (now - lastUpdateTimeRef.current) / MILLISECONDS_SECONDS_FACTOR;
        const interpolatedTime = Math.min(currentTime + elapsed, duration);
        setDisplayTime(interpolatedTime);
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    if (isPlaying && !isRestartingRef.current) {
      lastUpdateTimeRef.current = Date.now();
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isPlaying, currentTime, duration]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    isPlaying ? audioRef.current.pause() : audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const handleReplay = () => {
    if (!audioRef.current) return;

    isRestartingRef.current = true;

    // Cancel any existing animation first
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }

    audioRef.current.pause();
    audioRef.current.currentTime = 0;

    setDisplayTime(0);
    setCurrentTime(0);
    setIsPlaying(false);

    // Start playing after a brief delay to ensure the progress is reset
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
        lastUpdateTimeRef.current = Date.now();
        isRestartingRef.current = false;
      }
    }, REPLAY_DELAY);
  };

  const isEnded = currentTime >= duration && duration > 0 && !isPlaying;

  return (
    <Stack {...stackProps}>
      <audio ref={audioRef} src={audioUrl} />
      <Flex align="center" gap="md" w="100%">
        <UnstyledButton
          onClick={isEnded ? handleReplay : togglePlayPause}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0
          }}>
          {isEnded ? (
            <IconReload color={color} />
          ) : isPlaying ? (
            <IconPlayerPauseFilled color={color} />
          ) : (
            <IconPlayerPlayFilled color={color} />
          )}
        </UnstyledButton>
        <Flex gap={2} align="center" w="100%" sx={{ flex: 1 }}>
          <Progress
            size="xs"
            w="100%"
            color={color}
            value={(displayTime / duration) * 100}
            styles={{
              bar: { color: color },
              root: {
                backgroundColor: theme.other.getDimmedColor(color)
              }
            }}
          />
        </Flex>
        <Center style={{ minWidth: 35 }}>
          <Text size="xs" color={color}>
            {formatSecondsToMinutesAndSeconds(currentTime > 0 ? Math.floor(currentTime) : Math.floor(duration))}
          </Text>
        </Center>
      </Flex>
    </Stack>
  );
};
